/**
 * O quadro onde o signatário desenha a assinatura com o dedo, a caneta ou o mouse.
 *
 * Eventos de ponteiro (não de toque nem de mouse): um só caminho serve aos três, e o
 * `touch-action: none` impede que o dedo arraste a página em vez de desenhar. A resolução acompanha
 * a tela até 2x — acima disso o PNG passaria do limite que o servidor aceita sem ganhar nitidez.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

const ALTURA_CSS = 180;
const ESCALA_MAXIMA = 2;

export interface QuadroDeAssinaturaProps {
  /** Chamado a cada traço terminado com o PNG em data URL, e com `null` ao limpar. */
  onChange: (imagem: string | null) => void;
  disabled?: boolean;
}

export const QuadroDeAssinatura: React.FC<QuadroDeAssinaturaProps> = ({ onChange, disabled = false }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const desenhando = useRef(false);
  const ultimoPonto = useRef<{ x: number; y: number } | null>(null);
  const [vazio, setVazio] = useState(true);
  // Por ref: um `onChange` escrito inline na tela mudaria a cada render, refaria o `limpar` e
  // apagaria o desenho no meio.
  const avisar = useRef(onChange);
  useEffect(() => {
    avisar.current = onChange;
  }, [onChange]);

  /** Ajusta a resolução do canvas à largura atual. Redimensionar apaga o que estava desenhado. */
  const configurarCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const escala = Math.min(window.devicePixelRatio || 1, ESCALA_MAXIMA);
    canvas.width = Math.round(canvas.clientWidth * escala);
    canvas.height = Math.round(ALTURA_CSS * escala);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(escala, 0, 0, escala, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  const limpar = useCallback(() => {
    configurarCanvas();
    setVazio(true);
    avisar.current(null);
  }, [configurarCanvas]);

  useEffect(() => {
    configurarCanvas();
    let largura = canvasRef.current?.clientWidth ?? 0;
    // Girar o celular muda a largura; redesenhar do zero é mais honesto que esticar o traço. Só a
    // largura conta: no celular a barra de endereço some ao rolar e dispara `resize` mudando só a
    // altura da janela — limpar aí apagaria a assinatura no meio do desenho.
    const aoRedimensionar = (): void => {
      const atual = canvasRef.current?.clientWidth ?? 0;
      if (atual === largura) return;
      largura = atual;
      limpar();
    };
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, [configurarCanvas, limpar]);

  const pontoDo = (evento: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const caixa = evento.currentTarget.getBoundingClientRect();
    return { x: evento.clientX - caixa.left, y: evento.clientY - caixa.top };
  };

  const comecar = (evento: React.PointerEvent<HTMLCanvasElement>): void => {
    if (disabled) return;
    evento.currentTarget.setPointerCapture(evento.pointerId);
    desenhando.current = true;
    const ponto = pontoDo(evento);
    ultimoPonto.current = ponto;
    const ctx = evento.currentTarget.getContext('2d');
    if (!ctx) return;
    // Um toque sem arrastar também deixa marca: um ponto.
    ctx.beginPath();
    ctx.arc(ponto.x, ponto.y, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fillStyle = ctx.strokeStyle;
    ctx.fill();
  };

  const mover = (evento: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!desenhando.current || !ultimoPonto.current) return;
    const ctx = evento.currentTarget.getContext('2d');
    if (!ctx) return;
    const ponto = pontoDo(evento);
    ctx.beginPath();
    ctx.moveTo(ultimoPonto.current.x, ultimoPonto.current.y);
    ctx.lineTo(ponto.x, ponto.y);
    ctx.stroke();
    ultimoPonto.current = ponto;
  };

  const terminar = (evento: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!desenhando.current) return;
    desenhando.current = false;
    ultimoPonto.current = null;
    setVazio(false);
    avisar.current(evento.currentTarget.toDataURL('image/png'));
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          border: '1px dashed',
          borderColor: vazio ? 'divider' : 'primary.main',
          borderRadius: 1,
          bgcolor: '#fff',
        }}
      >
        <canvas
          ref={canvasRef}
          aria-label="Área para desenhar a assinatura"
          onPointerDown={comecar}
          onPointerMove={mover}
          onPointerUp={terminar}
          onPointerCancel={terminar}
          style={{ display: 'block', width: '100%', height: ALTURA_CSS, touchAction: 'none', cursor: disabled ? 'default' : 'crosshair' }}
        />
        {vazio && (
          <Typography
            variant="body2"
            // Cor fixa, e não do tema: o quadro é sempre branco (a assinatura sai em PNG sobre branco),
            // e no modo escuro o cinza do tema sumiria sobre ele.
            sx={{ color: '#9ca3af', position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}
          >
            Assine aqui com o dedo
          </Typography>
        )}
        <Box sx={{ position: 'absolute', left: 24, right: 24, bottom: 36, borderBottom: '1px solid #d1d5db', pointerEvents: 'none' }} />
      </Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
        <Button size="small" onClick={limpar} disabled={disabled || vazio}>
          Limpar
        </Button>
      </Stack>
    </Box>
  );
};

export default QuadroDeAssinatura;
